import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, defaultUsers } from '../data/houses';

interface AuthContextType {
  currentUser: User | null;
  login: (type: 'teacher' | 'mascot' | 'kiosk', houseId: string | null, password: string) => User | null;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'houseKioskAuth';

type AuthProviderProps = {
  children: ReactNode;
  overrideUser?: User | null;
  onLogout?: () => void;
};

export function AuthProvider({ children, overrideUser, onLogout }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    if (overrideUser !== undefined) {
      return overrideUser;
    }
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load auth state:', e);
    }
    return null;
  });

  useEffect(() => {
    if (overrideUser !== undefined) {
      setCurrentUser(overrideUser);
      return;
    }
    if (currentUser) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(currentUser));
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, [currentUser, overrideUser]);

  const login = (type: 'teacher' | 'mascot' | 'kiosk', houseId: string | null, password: string): User | null => {
    if (overrideUser !== undefined) {
      return null;
    }
    let user: User | undefined;

    if (type === 'teacher') {
      user = defaultUsers.find(u => u.type === 'teacher' && u.password === password);
    } else if (type === 'mascot' && houseId) {
      user = defaultUsers.find(u => u.type === 'mascot' && u.houseId === houseId && u.password === password);
    } else if (type === 'kiosk') {
      user = defaultUsers.find(u => u.type === 'kiosk' && u.password === password);
    }

    if (user) {
      setCurrentUser(user);
      return user;
    }
    return null;
  };

  const logout = () => {
    if (overrideUser !== undefined) {
      onLogout?.();
      return;
    }
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        login,
        logout,
        isAuthenticated: !!currentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
